import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  CardMedia,
  Skeleton,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import type { Product } from '../../types/product';
import { ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../cart/CartManager';
import CartConfirmationDialog from '../shop/CartConfirmationDialog';
import { useTranslation } from 'react-i18next';
import { getProductName, getProductDescription, getBrandName } from '../../utils/productUtils';

// Cart icon component
const CartIcon: React.FC<{ onClick: (e: React.MouseEvent) => void; disabled?: boolean }> = ({ onClick, disabled = false }) => (
  <img 
    src={disabled ? "/icons/ic_cart_empty.svg" : "/icons/ic_cart.svg"} 
    alt={disabled ? "Out of stock" : "Add to cart"} 
    style={{ 
      width: 20, 
      height: 20, 
      cursor: disabled ? 'not-allowed' : 'pointer', 
      marginLeft: 0.5,
      opacity: disabled ? 0.5 : 1,
      transition: 'opacity 0.2s ease'
    }}
    onClick={disabled ? undefined : onClick}
  />
);

const CARD_WIDTH_DESKTOP = 215;
const CARD_WIDTH_MOBILE = 140;
const SKELETON_COUNT = 5;

interface ProductListProps {
  products: Product[];
  loading: boolean;
  variant: 'horizontal' | 'grid';
  onViewMore?: () => void;
  title?: string;
  fullWidth?: boolean; // New prop to control container styling
}

// Reusable Product Card Component
const ProductCard: React.FC<{
  item: Product | number | unknown;
  loading: boolean;
  showFavorite?: boolean;
  onClick?: () => void;
  onBrandClick?: (brandId: number, brandName: string) => void;
}> = ({ item, loading, showFavorite = false, onClick, onBrandClick }) => {
  const { i18n } = useTranslation();
  const [imageError, setImageError] = React.useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [cartConfirmationOpen, setCartConfirmationOpen] = useState(false);
  const [addedProduct, setAddedProduct] = useState<Product | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { addToCart } = useCart();

  const handleImageError = () => {
    setImageError(true);
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent product click
    if (loading || isAddingToCart) return;
    
    const product = item as Product;
    if (!product.id) return;
    
    setIsAddingToCart(true);
    try {
      // Add to cart (will call API if user is logged in)
      await addToCart({
        id: product.id,
        quantity: 1,
        name: getProductName(product, i18n.language),
        price: product.price,
        imageUrl: product.imageUrl,
        description: getProductDescription(product, i18n.language),
        vendor: product.vendor || getBrandName(product, i18n.language),
        maxAvailable: product.inventory?.available || product.quantity,
      });
      
      // Show cart confirmation dialog instead of success message
      setAddedProduct({
        id: product.id,
        name: product.name,
        brand: product.brand,
        vendor: product.vendor,
        imageUrl: product.imageUrl,
        price: product.price,
        quantity: 1,
        description: product.description,
        imageGallery: product.imageGallery,
        comparePrice: product.comparePrice,
        nameZh: product.nameZh,
        nameCn: product.nameCn,
        descriptionZh: product.descriptionZh,
        descriptionCn: product.descriptionCn,
        brandInfo: product.brandInfo,
        categories: product.categories,
        trackQuantity: product.trackQuantity,
        chargeTax: product.chargeTax,
        continueSelling: product.continueSelling,
        type: product.type,
        brandId: product.brandId,
        collections: product.collections,
        tags: product.tags,
        variantOptions: product.variants ? product.variants.map(v => ({
          id: v.id,
          name: Object.keys(v.options)[0] || 'Option',
          values: Object.values(v.options)
        })) : undefined,
        variants: product.variants,
        isPhysicalProduct: product.isPhysicalProduct,
        weight: product.weight,
        weightUnit: product.weightUnit,
        status: product.status,
        onlineStore: product.onlineStore,
        pointOfSale: product.pointOfSale,
        ourPick: product.ourPick,
        eat365ProductId: product.eat365ProductId,
        createdAt: product.createdAt,
        category: product.category,
        inStock: product.inStock,
        inventory: product.inventory,
        sku: product.sku,
        barcode: product.barcode,
        costPerItem: product.costPerItem
      });
      setCartConfirmationOpen(true);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setIsAddingToCart(false);
    }
  };

  return (
    <Box
      onClick={loading ? undefined : onClick}
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0, // Allow shrinking below content size
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        borderRadius: 3,
        position: 'relative',
        cursor: loading ? 'default' : 'pointer',
        minHeight: loading ? (isMobile ? '150px' : '200px') : 'auto',
        boxSizing: 'border-box', // Ensure padding/borders are included in width calculation
        overflow: 'hidden', // Prevent any content overflow
      }}
    >
      {loading ? (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={isMobile ? '150px' : '200px'}
          sx={{ borderRadius: 3, aspectRatio: 1 }}
        />
      ) : (
        <CardMedia
          component="img"
          image={imageError || !(item as Product).imageUrl ? '/images/img_placeholder.svg' : (item as Product).imageUrl}
          alt={(item as Product).name}
          onError={handleImageError}
          sx={{
            width: '100%',
            objectFit: 'cover',
            borderRadius: 0.5,
            aspectRatio: 1,
          }}
        />
      )}
      
      {/* Stock Status Chip - only show when OUT OF STOCK */}
      {!loading && (item as Product).inventory && !(item as Product).inventory?.inStock && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#FFEBEE',
              color: '#C62828',
              padding: isMobile ? '4px 8px' : '6px 12px',
              borderRadius: '2px',
              fontSize: isMobile ? '10px' : '12px',
              fontWeight: 600,
              letterSpacing: '0.3px',
              minWidth: isMobile ? '50px' : '60px',
              textAlign: 'center',
              border: '1px solid #FFCDD2',
              // Enhanced visibility for desktop
              '@media (min-width: 768px)': {
                fontSize: '10px',
                padding: '3px 6px',
                minWidth: '70px',
                // Add subtle background overlay for better visibility
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '6px',
                  zIndex: -1,
                },
              },
            }}
                      >
              Sold Out
            </Box>
        </Box>
      )}
      
      {/* Only show favorite icon when not loading and showFavorite is true */}
      {!loading && showFavorite && (
        <IconButton
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: '#fff',
            boxShadow: 1,
            zIndex: 1,
          }}
          size="small"
        >
          <FavoriteBorderIcon fontSize="small" />
        </IconButton>
      )}
      <Box
        component="div"
        sx={{
          flex: 1,
          py: 1.5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'start',
        }}
      >
        {loading ? (
          <>
            <Skeleton width="60%" height={24} />
            <Skeleton width="80%" height={18} />
            <Skeleton width="100%" height={18} />
            <Skeleton width="40%" height={18} />
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box
                onClick={(e) => {
                  e.stopPropagation(); // Prevent product click
                  const product = item as Product;
                  if (product.brandId) {
                    onBrandClick?.(product.brandId, product.brand || 'Brand');
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: (item as Product).brandId ? 'pointer' : 'default',
                  transition: 'opacity 0.2s ease',
                  '&:hover': {
                    opacity: (item as Product).brandId ? 0.7 : 1,
                  },
                  flex: 1,
                }}
              >
                <Typography
                  sx={{
                    color: '#17110D',
                    fontSize: '14px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    wordBreak: 'break-word', // Allow breaking long words
                    overflow: 'hidden',
                    WebkitLineClamp: 1,
                    textWrap: 'wrap',
                    textOverflow: 'ellipsis',
                  }}
                  noWrap
                >
                  {getBrandName(item as Product, i18n.language) || (item as Product).vendor || 'Brand'}
                </Typography>

              </Box>
              <Tooltip title={isAddingToCart ? "Adding..." : "Add to cart"} placement="top">
                <span>
                  <CartIcon 
                    onClick={handleAddToCart} 
                    disabled={isAddingToCart || !(item as Product).inventory?.inStock} 
                  />
                </span>
              </Tooltip>
            </Box>
            <Typography
              sx={{
                color: '#3D3C3C',
                fontSize: '14px',
                fontWeight: 400,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word', // Allow breaking long words
                hyphens: 'auto', // Enable hyphenation
              }}
            >
              {getProductName(item as Product, i18n.language)}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                flexDirection: 'column',
                my: 1,
              }}
            >
              {(item as Product).comparePrice && ((item as Product)?.comparePrice ?? 0) > 0 && (
                <Typography
                  sx={{ textDecoration: 'line-through', color: theme.palette.custom.brown, fontSize: '12px', fontWeight: 400 }}
                >
                  HK${(item as Product).comparePrice?.toFixed(2)}
                </Typography>
              )}
              <Typography
                sx={{color: '#B31818', fontSize: '12px'}}
                fontWeight={700}
              >
                HK${(item as Product).price?.toFixed(2)}
              </Typography>
              
            </Box>
          </>
        )}
      </Box>
      
      {/* Cart Confirmation Dialog */}
      <CartConfirmationDialog
        open={cartConfirmationOpen}
        onClose={() => setCartConfirmationOpen(false)}
        product={addedProduct}
      />
    </Box>
  );
};

const ProductList: React.FC<ProductListProps> = ({
  products,
  loading,
  variant,
  onViewMore,
  title,
  fullWidth = false,
}) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const handleProductClick = (product: Product) => {
    console.log('Product clicked:', product);
    navigate(`/product/${product.id}`, { state: { product } });
  };

  const handleBrandClick = (brandId: number, brandName: string) => {
    navigate(`/brand/${brandId}/${encodeURIComponent(brandName)}`);
  };

  const scrollByCard = (direction: 'left' | 'right') => {
    if (listRef.current) {
      const cardWidth = isMobile ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP;
      const scrollAmount =
        direction === 'left' ? -cardWidth - 24 : cardWidth + 24;
      listRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  if (variant === 'horizontal') {
    return (
      <Box sx={{ mt: 1, maxWidth: 'lg', mx: 'auto', width: '100%', px: '15px'}}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Typography fontWeight={700} sx={{ ml: 1, fontSize: '18px' }}>
            {title}
          </Typography>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, mr: 1 }}>
            <IconButton
              aria-label="Scroll left"
              onClick={() => scrollByCard('left')}
              size="small"
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="Scroll right"
              onClick={() => scrollByCard('right')}
              size="small"
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Box
          ref={listRef}
          sx={{
            display: 'flex',
            gap: 3,
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: 2,
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            userSelect: 'none',
          }}
        >
          {(loading ? Array.from({ length: SKELETON_COUNT }) : products).map(
            (item, idx) => (
              <Box
                key={loading ? idx : (item as Product).id}
                sx={{
                  width: isMobile ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP,
                  flexShrink: 0, // Prevent shrinking
                }}
              >
                <ProductCard
                  item={item}
                  loading={loading}
                  showFavorite={false}
                  onClick={
                    !loading ? () => handleProductClick(item as Product) : undefined
                  }
                  onBrandClick={handleBrandClick}
                />
              </Box>
            )
          )}
        </Box>
        {onViewMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              sx={{
                width: '75%',
                height: 48,
                borderWidth: 1,
                gap: 1, // 16px (theme spacing unit is 8px)
                pt: 1.5, // 12px
                pr: 4, // 32px
                pb: 1.5, // 12px
                pl: 4, // 32px
                borderColor: theme.palette.custom.brown,
                borderRadius: '4px',
                display: 'flex',
                '&:hover': { background: '#F5F1E3', borderColor: '#B7A16A' },
              }}
              onClick={onViewMore}
            >
              <Typography
                sx={{ fontSize: 14, fontWeight: 400, color: theme.palette.custom.brown, whiteSpace: 'nowrap', pl: 0.5, textAlign: 'center' }}
              >
                {t('cart.viewMore')}
              </Typography>{' '}
              <ChevronRight sx={{ width: 24, height: 24, color: theme.palette.custom.brown }} />
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  // grid variant
  return (
    <Box sx={{ 
      mt: 1, 
      ...(fullWidth ? {} : { maxWidth: 'lg', mx: 'auto' }), 
      width: '100%', 
      px: '15px' 
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        {title && (
          <Typography fontWeight={700} sx={{ ml: 1, fontSize: '18px' }}>
            {title}
          </Typography>
        )}
      </Box>
      <Box 
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
          gap: '15px',
          paddingLeft: '5px',
          justifyContent: 'start',
          width: '100%',
          overflow: 'hidden', // Prevent any overflow issues
        }}
      >
        {(loading ? Array.from({ length: SKELETON_COUNT * 2 }) : products).map(
          (item, idx) => (
            <Box 
              key={loading ? idx : (item as Product).id} 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                width: '100%',
                minWidth: 0, // Allow shrinking below content size
                overflow: 'hidden', // Prevent content overflow
              }}
            >
                <ProductCard
                  item={item}
                  loading={loading}
                  showFavorite={false}
                  onClick={
                    !loading ? () => handleProductClick(item as Product) : undefined
                  }
                  onBrandClick={handleBrandClick}
                />
              </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default ProductList;
